// Timeout helpers for the character-export pipeline (ENG-600).
//
// The export does several serial network fetches (blueprint, rig GLB, clips,
// materials) against the asset-manager / S3. In some CI environments one of
// those requests can hang indefinitely instead of failing fast, which blows
// the whole Playwright test past its 2-minute timeout with no useful error.
//
// These helpers bound every export fetch so a hung request fails LOUD with a
// clear "timed out" message (CLAUDE.md: loud failure beats a silent hang).
// Kept in their own module — no vite-only `?raw` imports — so they are
// importable from node:test unit tests.

export const EXPORT_FETCH_TIMEOUT_MS = 30_000

/** Error thrown when an export step exceeds its time budget. */
export class ExportTimeoutError extends Error {
  constructor(label, ms) {
    super(`character export: ${label} timed out after ${ms}ms`)
    this.name = 'ExportTimeoutError'
  }
}

/**
 * Race a promise against a timeout. Rejects with ExportTimeoutError if the
 * promise hasn't settled within `ms`. Use for awaits we can't abort directly
 * (e.g. the asset-manager JSON list calls).
 *
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} label
 * @returns {Promise<T>}
 */
export function withTimeout(promise, ms, label) {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new ExportTimeoutError(label, ms)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

/**
 * fetch() bounded by an AbortController so a hung request is actually aborted
 * (not just abandoned). Throws ExportTimeoutError on timeout; re-throws other
 * fetch errors unchanged.
 *
 * @param {string} url
 * @param {number} ms
 * @param {string} label
 * @param {typeof fetch} [fetchImpl] injectable for tests
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(url, ms, label, fetchImpl = fetch) {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  try {
    return await fetchImpl(url, { signal: ctrl.signal })
  } catch (e) {
    if (e?.name === 'AbortError') throw new ExportTimeoutError(label, ms)
    throw e
  } finally {
    clearTimeout(timer)
  }
}
